import { z } from 'zod';
import {
  calculateDiscretionary,
  calculatePaa,
  calculatePaaCash,
  classifyGroup,
  validateTaaLine,
  type GroupSizeSettings,
} from '../awards/index.js';
import { codeLine, type ChargeCode, type CodingTables } from '../coding/index.js';
import { parsePlainDate } from '../dates/index.js';
import {
  assignTaaPools,
  paidFrom,
  poolForAward,
  type PoolTotal,
  type SpotFunding,
  type TaaAssignment,
  type TaaPool,
} from '../funding/index.js';
import type { AwardState } from '../lifecycle/index.js';
import { cents } from '../money/index.js';
import {
  failure,
  success,
  type AwardTypeCode,
  type Cents,
  type CostType,
  type Fraction,
  type PersonFacts,
  type PlainDate,
  type Pool,
  type Result,
} from '../types.js';
import { fractionIssue } from '../validation.js';
import { settingRegistry, valueAt, type EffectiveValue } from './index.js';

export interface SnapshotSettings {
  readonly fmvCents: readonly EffectiveValue<number>[];
  readonly cashShare: readonly EffectiveValue<Fraction>[];
  /** APP-151: `values.paaOptionShare` for PAA, by date. */
  readonly paaOptionShare: readonly EffectiveValue<Fraction>[];
  /** APP-151: `values.optionValueCents`, by date; empty until Areté enters it. */
  readonly optionValueCents: readonly EffectiveValue<number>[];
  readonly taaShare: Fraction;
  readonly minimumOptions: number;
  readonly groupSize: GroupSizeSettings;
}
export interface AwardSnapshot {
  readonly effectiveDate: PlainDate;
  readonly fmvCents: Cents;
  /** Before APP-151, a PAA's split of its total value. Kept so older awards stay as they were. */
  readonly cashShare: Fraction;
  /**
   * APP-151: a PAA's options as a share of its cash. Present on every snapshot taken since, and
   * its presence is what makes a PAA's amount its cash. Absent on older snapshots (jsonb).
   */
  readonly paaOptionShare?: Fraction;
  /**
   * APP-151: the value of one option on the snapshot date. Absent on older snapshots, and on one
   * taken while it wasn't set (a PAA can't be worked out until it is).
   */
  readonly optionValueCents?: Cents;
  readonly taaShare: Fraction;
  readonly minimumOptions: number;
  readonly groupSize: GroupSizeSettings;
  readonly tables: CodingTables;
}
const code = z.string().trim().min(1);
const rows = z.object({ OH: code, GA: code }).strict();
const tablesSchema = z
  .object({
    accounts: z.object({ discretionary: rows, paa_taa: rows, spot: rows }).strict(),
    poolProjects: z.record(code, rows),
    discretionaryOrg: code,
    spotOrg: z.discriminatedUnion('source', [
      z.object({ source: z.literal('home') }).strict(),
      z.object({ source: z.literal('fixed'), org: code }).strict(),
    ]),
  })
  .strict();

/** First submission uses award date, or submit date when no award date is set.
 * Only an explicitly authorized resnapshot replaces an existing saved snapshot. */
export function snapshotValues(
  settings: SnapshotSettings,
  tables: CodingTables,
  date: string,
): Result<AwardSnapshot> {
  const effectiveDate = parsePlainDate(date);
  if (!effectiveDate.ok) return effectiveDate;
  const fmv = valueAt(settings.fmvCents, date);
  if (!fmv.ok) return fmv;
  const split = valueAt(settings.cashShare, date);
  if (!split.ok) return split;
  const optionShare = valueAt(settings.paaOptionShare, date);
  if (!optionShare.ok) return optionShare;
  // Optional here: only a PAA needs it, and its recalculation refuses without it.
  const optionValue = valueAt(settings.optionValueCents, date);
  const values = [
    [settingRegistry['values.fmvCents'], fmv.value],
    [settingRegistry['values.cashShare'], split.value],
    [settingRegistry['values.paaOptionShare'], optionShare.value],
    ...(optionValue.ok
      ? ([[settingRegistry['values.optionValueCents'], optionValue.value]] as const)
      : []),
    [settingRegistry['taa.defaultSplit'], settings.taaShare],
    [settingRegistry['awards.minOptionsPerPerson'], settings.minimumOptions],
    [settingRegistry['awards.groupSize'], settings.groupSize],
  ] as const;
  if (values.some(([definition, value]) => !definition.schema.safeParse(value).success))
    return failure('invalid_snapshot_settings');
  const parsed = tablesSchema.safeParse(tables);
  if (!parsed.success) return failure('invalid_coding_tables');
  return success({
    effectiveDate: effectiveDate.value,
    fmvCents: cents(fmv.value),
    cashShare: { ...split.value },
    paaOptionShare: { ...optionShare.value },
    ...(optionValue.ok ? { optionValueCents: cents(optionValue.value) } : {}),
    taaShare: { ...settings.taaShare },
    minimumOptions: settings.minimumOptions,
    groupSize: { ...settings.groupSize },
    tables: parsed.data,
  });
}
export interface RecalculationLine {
  readonly person: PersonFacts;
  readonly totalValueCents?: number;
  readonly cashCents?: number;
  readonly options?: number;
  readonly poolId?: string;
  readonly costTypeOverride?: CostType;
}
export interface RecalculationRequest {
  readonly type: AwardTypeCode;
  readonly state: AwardState;
  readonly sectorId: string | null;
  readonly pools: readonly Pool[];
  readonly lines: readonly RecalculationLine[];
  readonly fundingMode: 'auto' | 'manual';
  readonly taaShareOverride?: Fraction;
  readonly cashShareOverride?: Fraction;
  /** A Spot Award's funding rule (`spot.fundingPool`); the Spot Awards pool when not given. */
  readonly spotFunding?: SpotFunding;
}
export interface RecalculatedLine {
  readonly personId: string;
  readonly poolId: string;
  readonly cashCents: Cents;
  readonly options: number;
  readonly totalValueCents: Cents;
  readonly grantedValueCents: Cents;
  readonly optionsRemainderCents: Cents;
  readonly coding: ChargeCode;
}
export interface RecalculatedAward {
  readonly snapshot: AwardSnapshot;
  readonly lines: readonly RecalculatedLine[];
  readonly paidFrom: readonly PoolTotal[];
  readonly taaAssignment?: TaaAssignment;
}

/** Managers persist PersonFacts with the draft, not a live directory object.
 * This function recalculates an editable award; finalized records use saved lines. */
export function recalculate(
  request: RecalculationRequest,
  snapshot: AwardSnapshot,
): Result<RecalculatedAward> {
  if (!['S0', 'S1a', 'S1b', 'S2', 'S3', 'S4'].includes(request.state))
    return failure('snapshot_frozen');
  const people = new Set<string>();
  for (const line of request.lines) {
    if (!line.person.personId.trim() || people.has(line.person.personId))
      return failure('duplicate_or_missing_recipient');
    people.add(line.person.personId);
    if (line.person.status !== 'active') return failure('recipient_inactive');
  }
  const group = classifyGroup(request.type, request.lines.length, snapshot.groupSize);
  if (!group.ok) return group;
  if (group.value !== 'ok') return failure(group.value);
  const pools = new Map(request.pools.map((pool) => [pool.id, pool]));
  if (pools.size !== request.pools.length) return failure('ambiguous_funding_pool');
  const split = request.cashShareOverride ?? snapshot.cashShare;
  if (fractionIssue(split, 'cashShare').length) return failure('invalid_fraction');
  let taaAssignment: TaaAssignment | undefined;
  if (request.type === 'TAA') {
    const input = request.lines.map((line) => ({
      personId: line.person.personId,
      cashCents: line.cashCents ?? 0,
      ...(line.poolId === undefined ? {} : { pool: pools.get(line.poolId)?.kind as TaaPool }),
    }));
    const assignment = assignTaaPools(
      input,
      request.taaShareOverride ?? snapshot.taaShare,
      request.fundingMode,
    );
    if (!assignment.ok) return assignment;
    taaAssignment = assignment.value;
  }
  const lines: RecalculatedLine[] = [];
  for (const line of request.lines) {
    let cashCents: Cents, options: number, totalValueCents: Cents, grantedValueCents: Cents;
    let optionsRemainderCents = cents(0);
    if (request.type === 'PAA' && snapshot.paaOptionShare !== undefined) {
      // APP-151: the amount is the cash; options on top at the value of one option.
      if (snapshot.optionValueCents === undefined) return failure('option_value_not_set');
      const result = calculatePaaCash({
        cashCents: line.cashCents ?? 0,
        optionValueCents: snapshot.optionValueCents,
        optionShare: snapshot.paaOptionShare,
      });
      if (!result.ok) return result;
      ({ cashCents, options, totalValueCents } = result.value);
      grantedValueCents = totalValueCents;
    } else if (request.type === 'PAA') {
      const result = calculatePaa({
        totalValueCents: line.totalValueCents ?? 0,
        fmvCents: snapshot.fmvCents,
        cashShare: split,
      });
      if (!result.ok) return result;
      ({ cashCents, options, optionsRemainderCents } = result.value);
      totalValueCents = cents(line.totalValueCents ?? 0);
      grantedValueCents = cents(cashCents + result.value.optionsGrantValueCents);
    } else if (request.type === 'TAA' || request.type === 'SPOT') {
      const result = validateTaaLine({
        cashCents: line.cashCents ?? 0,
        options: line.options ?? 0,
      });
      if (!result.ok) return result;
      ({ cashCents, options } = result.value);
      totalValueCents = cashCents;
      grantedValueCents = cashCents;
    } else {
      const result = calculateDiscretionary({
        cashCents: line.cashCents ?? 0,
        options: line.options ?? 0,
        fmvCents: snapshot.fmvCents,
        minimumOptions: snapshot.minimumOptions,
      });
      if (!result.ok) return result;
      ({ cashCents, options, totalValueCents } = result.value);
      grantedValueCents = totalValueCents;
    }
    let poolId: string;
    if (taaAssignment) {
      const assigned = taaAssignment.assignment.find(
        (item) => item.personId === line.person.personId,
      );
      const matching = request.pools.filter(
        (pool) =>
          pool.kind === assigned?.pool &&
          (pool.kind !== 'sector' || pool.sectorId === request.sectorId),
      );
      const match = matching[0];
      if (!match || matching.length !== 1) return failure('missing_or_ambiguous_funding_pool');
      poolId = match.id;
      if (request.fundingMode === 'manual' && line.poolId !== poolId)
        return failure('invalid_manual_funding_pool');
    } else {
      const selected = poolForAward({
        awardType: request.type,
        sectorId: request.sectorId,
        pools: request.pools,
        nomineeSectorId: line.person.sectorId,
        ...(request.spotFunding === undefined ? {} : { spotFunding: request.spotFunding }),
      });
      if (!selected.ok) return selected;
      poolId = selected.value;
    }
    const pool = pools.get(poolId);
    if (!pool) return failure('missing_funding_pool');
    const coding = codeLine({
      awardType: request.type,
      poolOfLine: pool,
      person: line.person,
      tables: snapshot.tables,
      ...(line.costTypeOverride === undefined ? {} : { costTypeOverride: line.costTypeOverride }),
    });
    if (!coding.ok) return coding;
    lines.push({
      personId: line.person.personId,
      poolId,
      cashCents,
      options,
      totalValueCents,
      grantedValueCents,
      optionsRemainderCents,
      coding: coding.value,
    });
  }
  const totals = paidFrom(lines);
  if (!totals.ok) return totals;
  return success({
    snapshot,
    lines,
    paidFrom: totals.value,
    ...(taaAssignment === undefined ? {} : { taaAssignment }),
  });
}
